import { useEffect, useRef, useState } from "react";

export interface PresenceEntry<T> {
  key: string;
  item: T;
  isExiting: boolean;
}

/**
 * Minimal "AnimatePresence" for a keyed list (the app ships no animation
 * library). When an item disappears from `items`, this keeps it in the rendered
 * output for `exitMs` longer, flagged `isExiting`, so the row can play an exit
 * animation before React unmounts it.
 *
 * Present rows are derived live from `items` on every render (no extra lag, so
 * a moving progress indicator stays in sync). Only the small set of *exiting*
 * rows is held in state, so steady-state ticks don't add re-renders. An exiting
 * row carries the frozen item data it had at the moment it left, which keeps its
 * identity stable — the memoized row component skips re-renders mid-animation.
 *
 * If a key reappears before its exit timer fires (e.g. a quick leg flip back),
 * the timer is cancelled and the row simply continues as present.
 */
export function useExitTransition<T>(
  items: T[],
  getKey: (item: T) => string,
  exitMs: number
): PresenceEntry<T>[] {
  const [exiting, setExiting] = useState<PresenceEntry<T>[]>([]);
  const prevKeysRef = useRef<Set<string>>(new Set());
  const prevItemsRef = useRef<Map<string, T>>(new Map());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  useEffect(() => {
    const currentKeys = new Set(items.map(getKey));

    // A key that came back before its exit finished: cancel the timer and drop
    // it from the exiting set so it renders as a normal present row again.
    let cameBack = false;
    for (const key of currentKeys) {
      const timer = timersRef.current.get(key);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(key);
        cameBack = true;
      }
    }
    if (cameBack) {
      setExiting((ex) => ex.filter((e) => !currentKeys.has(e.key)));
    }

    // Keys present at the previous commit but gone now → begin exiting.
    const newlyGone: PresenceEntry<T>[] = [];
    for (const key of prevKeysRef.current) {
      if (!currentKeys.has(key) && !timersRef.current.has(key)) {
        const item = prevItemsRef.current.get(key);
        if (item !== undefined) {
          newlyGone.push({ key, item, isExiting: true });
        }
      }
    }
    if (newlyGone.length) {
      setExiting((ex) => {
        const have = new Set(ex.map((e) => e.key));
        return [...ex, ...newlyGone.filter((e) => !have.has(e.key))];
      });
      for (const entry of newlyGone) {
        const timer = setTimeout(() => {
          timersRef.current.delete(entry.key);
          setExiting((ex) => ex.filter((x) => x.key !== entry.key));
        }, exitMs);
        timersRef.current.set(entry.key, timer);
      }
    }

    prevKeysRef.current = currentKeys;
    prevItemsRef.current = new Map(items.map((it) => [getKey(it), it]));
  }, [items, getKey, exitMs]);

  // Clear any pending timers on unmount.
  useEffect(
    () => () => {
      for (const timer of timersRef.current.values()) clearTimeout(timer);
      timersRef.current.clear();
    },
    []
  );

  const presentKeys = new Set(items.map(getKey));
  const result: PresenceEntry<T>[] = items.map((it) => ({
    key: getKey(it),
    item: it,
    isExiting: false,
  }));
  // Append still-exiting rows that haven't reappeared as present.
  for (const entry of exiting) {
    if (!presentKeys.has(entry.key)) result.push(entry);
  }
  return result;
}
